import {
  createAdminApiFailure,
  createAdminApiSuccess,
} from "../../shared/api/adminApiErrors";
import type {
  AdminWorkspaceAccessResult,
  SubmitWorkspaceAccessRequestRequest,
  SubmitWorkspaceAccessRequestResponse,
  WorkspaceAccessLookupRequest,
  WorkspaceAccessLookupResponse,
} from "./types/adminWorkspaceAccess.api";
import { classifySignupEmailDomain } from "../signupDomainRules";

const EXISTING_WORKSPACE_DIRECTORY = new Map<string, string>([
  ["metaphysical-labs.com", "Metaphysical Labs"],
]);

export function lookupWorkspaceAccessByEmail(
  request: WorkspaceAccessLookupRequest,
): AdminWorkspaceAccessResult<WorkspaceAccessLookupResponse> {
  const emailClassification = classifySignupEmailDomain(request.email);

  if (emailClassification.kind === "unknown") {
    return createAdminApiSuccess({
      classification: "unknown",
      requiresRequestAccess: false,
      workspaceName: null,
      domain: null,
    });
  }

  if (emailClassification.kind === "personal") {
    return createAdminApiSuccess({
      classification: "personal",
      requiresRequestAccess: false,
      workspaceName: null,
      domain: emailClassification.domain,
    });
  }

  const workspaceName =
    EXISTING_WORKSPACE_DIRECTORY.get(emailClassification.domain) ?? null;

  return createAdminApiSuccess({
    classification: workspaceName ? "company_existing" : "company_new",
    requiresRequestAccess: Boolean(workspaceName),
    workspaceName,
    domain: emailClassification.domain,
  });
}

export async function submitWorkspaceAccessRequest(
  request: SubmitWorkspaceAccessRequestRequest,
): Promise<AdminWorkspaceAccessResult<SubmitWorkspaceAccessRequestResponse>> {
  const lookupResult = lookupWorkspaceAccessByEmail({ email: request.email });

  if (!lookupResult.ok) {
    return lookupResult;
  }

  if (!lookupResult.data.requiresRequestAccess) {
    return createAdminApiFailure({
      code: "WORKSPACE_REQUEST_NOT_REQUIRED",
      message:
        "This email does not map to an existing workspace request-access flow.",
      retryable: false,
    });
  }

  // The request-access flow is intentionally separated from account creation.
  // Backend email delivery and approval persistence can replace this adapter later
  // without forcing the auth UI to change shape.
  return createAdminApiSuccess({
    status: "submitted",
    deliveryState: "email_queued",
    workspaceName: lookupResult.data.workspaceName,
  });
}
